/*
 *
 * UserList
 *
 */

import { useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { FormattedMessage, useIntl } from 'react-intl';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FloatingActionButton from '@mui/material/Fab';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { grey } from '@mui/material/colors';

import Spacings from '~/modules/ui-kit/spacings';
import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import {
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchableUserTable from './SearchableUserTable';
import messages from './messages';
import styles from './styles.module.css';

const route = getRouteApi('/users/');

export const UserList = () => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const search = route.useSearch();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  const handleClose = () => {
    setActionMenuAnchor(null);
  };

  const debouncedHandleSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        navigate({
          to: '/users',
          search: { ...search, search: searchValue, page: 0 },
        });
      }, 300),
    [navigate, search]
  );

  const handlePageChanged = (page: number) => {
    navigate({
      to: '/users',
      search: { ...search, page },
    });
  };

  const createUser = () => {
    navigate({ to: '/users/create' });
  };

  const editUser = (userId: number) => {
    navigate({
      to: '/users/$publisherId',
      params: { publisherId: String(userId) },
    });
  };

  const getInitialPage = () => {
    return search.page ?? 0;
  };

  const roles: string[] = useMemo(() => {
    if (Array.isArray(search.role)) {
      return search.role;
    } else if (search.role) {
      return [search.role];
    }
    return [];
  }, [search.role]);

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <DrawerButton />
            <Typography
              variant="h6"
              style={{ marginLeft: theme.spacing(2), flexGrow: 1 }}
            >
              <FormattedMessage {...messages.title} />
            </Typography>
            <Spacings.Inline scale="l" alignItems="center">
              {isDesktop ? (
                <Button
                  startIcon={<FileCopyIcon />}
                  href="/api/publishers/export/xlsx"
                  download
                  color="inherit"
                >
                  <FormattedMessage {...messages.downloadXlsx} />
                </Button>
              ) : null}
              <IconButton
                aria-label={intl.formatMessage(messages.showDownloadMenu)}
                aria-controls="action-menu"
                aria-haspopup="true"
                onClick={(event) => {
                  setActionMenuAnchor(event.currentTarget);
                }}
                color="inherit"
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="long-menu"
                anchorEl={actionMenuAnchor}
                keepMounted
                open={Boolean(actionMenuAnchor)}
                onClose={handleClose}
              >
                {!isDesktop ? (
                  <MenuItem href="/api/publishers/export/xlsx" download>
                    <FormattedMessage {...messages.downloadXlsx} />
                  </MenuItem>
                ) : null}
                <MenuItem
                  component="a"
                  href="/api/publishers/export/vcards"
                  download
                >
                  <FormattedMessage {...messages.downloadVCard} />
                </MenuItem>
              </Menu>
            </Spacings.Inline>
          </Toolbar>
        </AppBar>
      }
      floatingActionButton={
        <FloatingActionButton
          aria-label={intl.formatMessage(messages.createPublisher)}
          className={styles['floating-action-button']}
          onClick={createUser}
          color="secondary"
        >
          <AddIcon />
        </FloatingActionButton>
      }
    >
      <Spacings.Inset scale="s">
        <Spacings.Stack>
          <div className={styles.searchWrapper}>
            <SearchIcon
              style={{
                color: grey[500],
              }}
            />
            <input
              defaultValue={search.search}
              className={styles.searchItem}
              placeholder={intl.formatMessage(messages.typeToSearch)}
              onKeyUp={(e) => debouncedHandleSearch(e.currentTarget.value)}
              data-test="search-input"
            />
          </div>
        </Spacings.Stack>
      </Spacings.Inset>
      <SearchableUserTable
        query={search.search || ''}
        initialPage={getInitialPage()}
        onChangePage={handlePageChanged}
        onEditUser={editUser}
        filter={[
          `congregation=${search.congregation || ''}`,
          `state=${search.state || ''}`,
          `pioneerLevel=${search.pioneerLevel || ''}`,
          `privilege=${search.privilege || ''}`,
          `experienceLevel=${search.experienceLevel || ''}`,
        ].concat(roles.map((role) => `${role}=true`))}
      />
    </ContentView>
  );
};

export default UserList;
